import { useModals } from '@/components/modals/ModalProvider';
import { useUserData } from '@/libs/fleet-api/hooks';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	approvePipelineCH,
	getPipelineList,
	PipelineItems,
	rejectPipelineCH,
} from '@/libs/fleet-api/services/pipeline';
import { InfoCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Flex,
	Form,
	Input,
	notification,
	Row,
	Select,
	Table,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FC, Fragment, useEffect, useState } from 'react';
import { getMasterData } from '@/libs/fleet-api/services/master-data';
import fetcher, { FleetQueryParams } from '@/libs/fleet-api/fetcher';
import formRule from '@/libs/antd/rules';
import dayjs from 'dayjs';
import { formatCurrency } from '@/libs/antd/formater';
import GenericErrorMessage from '@/components/GenericErrorMessage';

const isEditable = (pipelineStatus: string): boolean =>
	pipelineStatus === 'PFHAP';

type CHPipelineListPageNonJFProps = {
	prospectId: string;
};

const CHPipelineListPageNonJF: FC<CHPipelineListPageNonJFProps> = () => {
	const { groupName } = useUserData() ?? {};
	const { openCustomerDetail, openPipelineModal } = useModals();
	const [checkedPipelineIds, setCheckedPipelineIds] = useState<number[]>([]);
	const [isApprovalModalOpen, setIsApprovalModalOpen] =
		useState<boolean>(false);
	const [isRejectionModalOpen, setIsRejectionModalOpen] =
		useState<boolean>(false);

	const searchForm = useForm<{
		by: 'period' | 'customer-name' | 'status';
		value: string;
	}>();

	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');

	const updateDateValue = (newStartDate: string, newEndDate: string) => {
		const formattedValue = `${newStartDate}|${newEndDate}`;
		searchForm.form.setFieldsValue({ ['value']: formattedValue });
	};

	const { data: prospectGroupStatus } = useQuery({
		queryKey: ['getMasterData'],
		queryFn: () => {
			return getMasterData('prospect-group-status');
		},
	});

	const { form: rejectionForm, fieldName: fieldNameRejection } = useForm<{
		pipelineHotProspectIds: number[];
		approvalNotes: string;
	}>();

	const { form: approvalForm, fieldName: fieldNameApproval } = useForm<{
		pipelineHotProspectIds: number[];
		approvalNotes: string;
	}>();

	const { data, isFetching, refetch, isError } = useQuery({
		queryKey: ['getPipelineList'],
		queryFn: () => {
			const params: FleetQueryParams = {
				pagination: { pageSize: 20, pageNumber: 1 },
				filter: {
					by: 'plafon-type',
					value: 'NonJoinFinancing',
				},
				...(searchForm.value?.by || searchForm.value?.value
					? {
							search: {
								by: searchForm.value?.by ?? '',
								value: searchForm.value?.value ?? '',
							},
						}
					: {}),
			};
			return getPipelineList(params);
		},
	});

	const [spreadData, setSpreadData] = useState<PipelineItems[]>([]);
	const dataSource = data?.data.items;
	useEffect(() => {
		if (dataSource) {
			const items = dataSource;
			const newPlafons: PipelineItems[] = [];
			items.forEach((item) => {
				const plafons =
					item.prospectInformation?.financingApplicationLimitInformation ?? [];
				if (plafons.length) {
					plafons.forEach((plafon) => {
						const spreadData = {
							napNo: item.napNo,
							pipelineNo: item.pipelineNo,
							pipelineHotProspectId: item.pipelineHotProspectId,
							prospectId: item.prospectId,
							customerName: item.customerName,
							customerId: item.customerId,
							customerGroup: item.customerGroup,
							existingFinancingLimit: plafon?.existingFinancingLimit,
							industrySector: item.industrySector,
							outstandingPrincipal: plafon?.outstandingPrincipal,
							limitApplication: plafon.limitApplication,
							financingPurpose: plafon.financingPurpose,
							plafonType: plafon.plafonType!,
							referrer: plafon.referrer,
							orderSource: plafon.orderSource,
							referrerUnitName: item.referrerUnitName,
							prospectStatus: item.prospectStatus,
							ultimateOwner: item.ultimateOwner,
							currentIssue: item.currentIssue,
							creditScheme: item.creditScheme,
							newOrderDate: item.newOrderDate,
							pipelineStatus: item.pipelineStatus,
							segmentMarket: plafon.segmentMarket,
							frmName: item.frmName,
							businessHeadName: item.businessHeadName,
						};
						newPlafons.push(spreadData);
					});
				}
			});
			setSpreadData(newPlafons);
		}
	}, [dataSource]);

	const updateCheckedPipelineIds = (
		pipelineHotProspectId: number,
		isChecked: boolean,
	) => {
		if (isChecked) {
			// Add the ID to the list if it's checked
			setCheckedPipelineIds((prev) => [...prev, pipelineHotProspectId]);
		} else {
			// Remove the ID from the list if it's unchecked
			setCheckedPipelineIds((prev) =>
				prev.filter((id) => id !== pipelineHotProspectId),
			);
		}
	};

	const approveCHPipelineMutation = useMutation({
		mutationFn: approvePipelineCH,
	});

	const rejectCHPipelineMutation = useMutation({
		mutationFn: rejectPipelineCH,
	});

	const onApprove = async (values: {
		pipelineHotProspectIds: number[];
		approvalNotes: string;
	}) => {
		try {
			const { approvalNotes } = values;
			await approveCHPipelineMutation.mutateAsync({
				approvalNotes,
				pipelineHotProspectIds: checkedPipelineIds,
			});
			approvalForm.resetFields();
			refetch();
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	const onReject = async (values: {
		pipelineHotProspectIds: number[];
		approvalNotes: string;
	}) => {
		try {
			const { approvalNotes } = values;
			await rejectCHPipelineMutation.mutateAsync({
				approvalNotes,
				pipelineHotProspectIds: checkedPipelineIds,
			});
			rejectionForm.resetFields();
			refetch();
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	if (isError) return <GenericErrorMessage />;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<Divider />
			<FleetTitle>Pipeline Management</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={() => refetch()}
				form={searchForm.form}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							label={'Search By'}
							labelCol={{ flex: 'auto' }}
							wrapperCol={{ style: { width: 250 } }}
							name={searchForm.fieldName('by')}
							rules={[formRule.required()]}
						>
							<Select
								placeholder="Search By"
								options={[
									{ label: 'Periode', value: 'period' },
									{ label: 'Nama Customer', value: 'customer-name' },
									{ label: 'Status Pengajuan', value: 'status' },
								]}
								onChange={(value) => {
									searchForm.form.setFieldsValue({ ['value']: '' });
									searchForm.form.setFieldsValue({ ['by']: value });
								}}
								style={{ display: 'block', width: '100%' }}
								onClear={async () => {
									searchForm.form.resetFields();
									await refetch();
								}}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						{searchForm.value?.by === 'period' ? (
							<Flex gap={12}>
								<Form.Item
									name={searchForm.fieldName('value')}
									hidden
									noStyle
									rules={[formRule.required()]}
								/>
								<Form.Item
									name="startDate"
									rules={[formRule.required()]}
									style={{ flex: 1 }}
								>
									<DatePicker
										onChange={(date) => {
											const newStartDate = date.format('YYYY-MM-DD');
											setStartDate(newStartDate);
											updateDateValue(newStartDate, endDate);
										}}
									/>
								</Form.Item>
								<Form.Item
									name="endDate"
									rules={[formRule.required()]}
									style={{ flex: 1 }}
								>
									<DatePicker
										onChange={(date) => {
											const newEndDate = date.format('YYYY-MM-DD');
											setEndDate(newEndDate);
											updateDateValue(startDate, newEndDate);
										}}
									/>
								</Form.Item>
							</Flex>
						) : searchForm.value?.by === 'status' ? (
							<Form.Item
								name={searchForm.fieldName('value')}
								rules={[formRule.required()]}
							>
								<Select options={prospectGroupStatus?.data} />
							</Form.Item>
						) : (
							<Form.Item
								name={searchForm.fieldName('value')}
								rules={[formRule.required()]}
							>
								<Input
									placeholder="Search..."
									style={{ display: 'block' }}
								/>
							</Form.Item>
						)}
					</Col>
					<Col>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Divider />

			<Table
				scroll={{ x: 'max-content' }}
				bordered
				dataSource={spreadData}
				loading={isFetching}
				columns={[
					{
						width: 1,
						title: 'No',
						align: 'center',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, _record, index) => index + 1,
					},
					{
						title: 'Pipeline ID',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openPipelineModal(record.pipelineHotProspectId!);
								}}
							>
								{record.pipelineNo}
							</a>
						),
					},
					...(groupName !== 'CR'
						? [
								{
									title: 'Submit Forum Pipeline',
									width: 1,
									onCell: (record: PipelineItems, index?: number) => {
										if (
											record.prospectId === spreadData[index! + 1]?.prospectId
										) {
											return { rowSpan: 2 };
										}
										if (
											record.prospectId === spreadData[index! - 1]?.prospectId
										) {
											return { rowSpan: 0 };
										}

										return {};
									},
									align: 'center' as const,
									render: (_: unknown, record: PipelineItems) => {
										if (!isEditable(record.pipelineStatus.id))
											return (
												<Checkbox
													disabled
													checked
												/>
											);
										return (
											<Checkbox
												onChange={(e) =>
													updateCheckedPipelineIds(
														record.pipelineHotProspectId!,
														e.target.checked,
													)
												}
											/>
										);
									},
								},
							]
						: []),
					{
						title: 'No. NAP',

						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.napNo,
					},
					{
						title: 'Nama Customer',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(record.customerId);
								}}
							>
								{record.customerName}
							</a>
						),
					},
					{
						title: 'Group Customer BMPP',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.customerGroup?.value,
					},

					{
						title: 'Status',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) =>
							record.existingFinancingLimit > 0 ? 'Existing' : 'New',
					},
					{
						title: 'Kelas Industri',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.industrySector?.industryClass,
					},
					{
						title: 'Portofolio Guideline',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						align: 'center',
						render: (_, record) => record.industrySector?.portofolioGuideline,
					},
					{
						title: 'Nama FRM',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.frmName,
					},

					{
						title: 'Nama FBDH',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.businessHeadName,
					},
					{
						title: 'Status Pengajuan',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.prospectStatus?.value,
					},
					{
						title: 'Status Pengajuan',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						align: 'center',
						render: (_, record) => record.prospectStatus?.value,
					},
					{
						title: 'Outstanding Principal',
						align: 'right',
						render: (_, record) =>
							record.outstandingPrincipal
								? formatCurrency(record.outstandingPrincipal)
								: '-',
					},
					{
						title: 'Plafond Existing',
						align: 'right',
						render: (_, record) =>
							record.existingFinancingLimit
								? formatCurrency(record.existingFinancingLimit)
								: '-',
					},
					{
						title: 'Plafond Diajukan',
						align: 'right',
						render: (_, record) =>
							record.limitApplication
								? formatCurrency(record.limitApplication)
								: '-',
					},
					{
						title: 'Sumber Order',
						render: (_, record) => record.orderSource?.value,
					},
					{
						title: 'Detail Sumber Order',
						render: (_, record) => record.referrer?.unitName,
					},
					{
						title: 'Ultimate Owner',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.ultimateOwner,
					},
					{
						title: 'Current Issue',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => (
							<span style={{ whiteSpace: 'pre-wrap' }}>
								{record.currentIssue ?? '-'}
							</span>
						),
					},
					{
						title: 'Skema Kredit',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => (
							<span style={{ whiteSpace: 'pre-wrap' }}>
								{record.creditScheme ?? '-'}
							</span>
						),
					},
					{
						title: 'New Order Date',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) =>
							dayjs(record.newOrderDate).format('DD-MM-YYYY'),
					},
					{
						title: 'Status Pipeline',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						align: 'center',
						render: (_, record) => record.pipelineStatus.value,
					},
				]}
			/>
			<Divider />

			<Flex
				justify="flex-end"
				gap={12}
			>
				{groupName !== 'CR' && (
					<>
						<Button
							type="primary"
							style={{
								width: 200,
								display: 'block',
								marginTop: 30,
								borderColor: '#1557BC',
								background: 'white',
								color: '#1557BC',
								fontWeight: 600,
							}}
							onClick={() => setIsRejectionModalOpen(true)}
							disabled={!checkedPipelineIds.length}
						>
							REJECT
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							style={{
								width: 200,
								display: 'block',
								marginTop: 30,
							}}
							disabled={!checkedPipelineIds.length}
							onClick={() => setIsApprovalModalOpen(true)}
						>
							APPROVE
						</Button>
					</>
				)}
			</Flex>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={rejectionForm}
				name="rejectionForm"
				onFinish={(values) => onReject(values)}
			>
				<FleetModal
					open={isRejectionModalOpen}
					okText="YA"
					onOk={async () => {
						const isValid = await rejectionForm.validateFields();
						if (isValid) {
							setIsRejectionModalOpen(false);
							rejectionForm.submit();
						}
					}}
					onCancel={() => {
						setIsRejectionModalOpen(false);
					}}
					cancelText="TIDAK"
				>
					<Flex
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							minHeight: 200,
							flexDirection: 'column',
							gap: 24,
						}}
					>
						<InfoCircleFilled
							style={{ color: '#1557BC', fontSize: 72 }}
							size={72}
						/>
						<span style={{ fontWeight: 600, fontSize: 16 }}>
							Apakah Anda Yakin Ingin Melakukan Rejection Pipeline?
						</span>

						<Form.Item
							name={fieldNameRejection('approvalNotes')}
							style={{ width: '80%' }}
							rules={[formRule.required()]}
						>
							<TextArea
								rows={3}
								placeholder="Rejection Notes"
							/>
						</Form.Item>
					</Flex>
				</FleetModal>
			</Form>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={approvalForm}
				name="approvalForm"
				onFinish={(values) => onApprove(values)}
			>
				<FleetModal
					open={isApprovalModalOpen}
					okText="YA"
					onOk={async () => {
						const isValid = await approvalForm.validateFields();
						if (isValid) {
							setIsApprovalModalOpen(false);
							approvalForm.submit();
						}
					}}
					onCancel={() => {
						setIsApprovalModalOpen(false);
					}}
					cancelText="TIDAK"
				>
					<Flex
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							minHeight: 200,
							flexDirection: 'column',
							gap: 24,
						}}
					>
						<InfoCircleFilled
							style={{ color: '#1557BC', fontSize: 72 }}
							size={72}
						/>
						<span style={{ fontWeight: 600, fontSize: 16 }}>
							Apakah Anda Yakin Ingin Melakukan Approval Pipeline?
						</span>

						<Form.Item
							name={fieldNameApproval('approvalNotes')}
							style={{ width: '80%' }}
							rules={[formRule.required()]}
						>
							<TextArea
								rows={3}
								placeholder="Approval Notes"
							/>
						</Form.Item>
					</Flex>
				</FleetModal>
			</Form>
		</Fragment>
	);
};

export default CHPipelineListPageNonJF;
